<template>
    <ElevatedCard :widthType="'third'">
        <HeaderWidget :iconUrl="loadIcon" @click="navigateTo('/facturation')" :title="widget.libelle" />
        <div class="regles-widget-group">
            <ReglePastille cle="dashboard.widgetFacturationDroit" />
            <ReglePastille cle="dashboard.widgetPerimetreCommun" />
        </div>
        <div class='site-widget-container'>
            <span v-if='widget.sites == null || widget.sites.length > 1' class='site-widget'>{{$t('multisites')}}</span>
            <span v-else v-for='site in widget.sites' :key='site.id' class='site-widget'>
                {{site.description}}
                <span v-if='site.id !== widget.sites[widget.sites.length - 1].id'>, </span>
            </span>
        </div>
        <NoData
            v-if="
                !isLoading &&
                !getBillingData.nbEnAttente &&
                !getBillingData.nbMaxJourRetard &&
                !getBillingData.nbEnRetard &&
                !getBillingData.nbPayees
            "
            class="no-data"
            textNoData="widget.billing.noData"
        />
        <div v-else class="card-content">
            <div class="tiles-container">
                <Tile
                    class="tile"
                    :value="getBillingData.nbEnRetard"
                    :titleNotBold="$t('widgetBilling.facture', getBillingData.nbEnRetard)"
                    :titleInBold="$t('widgetBilling.retard')"
                    :titleEnd="$t('widgetBilling.paiement')"
                    :color="'--erreur'"
                    :fontSize="'19px'"
                    :isLoading="isLoading"
                    @click="navigateTo('/facturation/')"
                />
                <Tile
                    class="tile"
                    :value="getBillingData.nbEnAttente"
                    :titleNotBold="$t('widgetBilling.facture', getBillingData.nbEnAttente)"
                    :titleInBold="$t('widgetBilling.attente')"
                    :titleEnd="$t('widgetBilling.paiement')"
                    :color="'--en-attente'"
                    :fontSize="'19px'"
                    :isLoading="isLoading"
                    @click="navigateTo('/facturation/echeance')"
                />
                <Tile
                class="tile"
                :value="getBillingData.nbPayees"
                :titleNotBold="$t('widgetBilling.facture', getBillingData.nbPayees)"
                :titleInBold="$t('widgetBilling.payees')"
                :color="'--fait'"
                :fontSize="'19px'"
                :isLoading="isLoading"
                @click="navigateTo('/facturation/payee')"
                />
            </div>
            <ContentLoader width="150" height="70" v-if="isLoading" />
            <div v-else>
                <div class="text">
                    {{ $t('widgetBilling.jourEnRetard') }}
                    <span class="text accent">
                        {{ $t('widgetBilling.nbJourEnRetard', { count: getBillingData.nbMaxJourRetard || 0 }) }}
                    </span>
                </div>
            </div>
        </div>
    </ElevatedCard>
</template>

<script lang='ts'>
import { defineComponent, PropType } from 'vue';
import ElevatedCard from '../../../components/ui-components/ElevatedCard.vue';
import HeaderWidget from '../../../components/ui-components/HeaderWidget.vue';
import Tile from '../../../components/ui-components/Tile.vue';
import HomeService from '../services/HomeService';
import BillingWidgetItems from '@/models/BillingWidgetItems';
import { ContentLoader } from 'vue-content-loader';
import NoData from '@/components/ui-components/NoDataWidget.vue';
import { Widget } from '@/models/Widget';
import ReglePastille from '@/components/regles/ReglePastille.vue';

export default defineComponent({
    name: 'Billing',
    components: { HeaderWidget, Tile, ElevatedCard, ContentLoader, NoData, ReglePastille },
    props: {
        widget:{ type: Object as PropType<Widget>, required: true },
    },
    data() {
        return {
            // Compteurs propres à ce widget (le state Vuex est partagé entre tous les widgets)
            widgetItems: { nbEnRetard: 0, nbEnAttente: 0, nbMaxJourRetard: 0, nbPayees: 0 } as BillingWidgetItems,
            widgetLoading: false,
            widgetError: false,
        };
    },
    async mounted() {
        this.widgetLoading = true;
        try {
            const response = await HomeService.getBillingData({
                siteIds: this.widget.sites?.map(site => site.id) ?? [],
                chantierIds: this.chantierIds,
            });
            this.widgetItems = response.data;
        } catch (e) {
            this.widgetError = true;
        } finally {
            this.widgetLoading = false;
        }
    },
    methods: {
        navigateTo(path: string) {
            // Ouvre la liste sur les sites du widget
            this.$router.push(this.chantierIds.length > 0 ? { path, query: { chantierIds: this.chantierIds.join(',') } } : path);
        },
    },
    computed: {
        chantierIds(): number[] {
            return this.widget.sites?.map(site => site.chantierId) ?? [];
        },
        isLoading(): boolean {
            return this.widgetLoading;
        },
        isError(): boolean {
            return this.widgetError;
        },
        getBillingData(): BillingWidgetItems {
            return this.widgetItems;
        },
        loadIcon() {
            return require('@/assets/img/menu/receipt_long_45.png');
        },
    },
});
</script>

<style scoped>
.regles-widget-group {
    display: flex;
    align-items: center;
    gap: 16px;
}
.tiles-container {
    display: flex;
    justify-content: space-around;
    margin-bottom: 7px;
    width: 90%;
    min-height: 30%;
    gap: 25px;
}
@media screen and (max-width: 1600px) {
    .tiles-container {
        width: 100%;
        gap: 10px
    }
}
.tile {
    cursor: pointer;
    height: 125px;
    max-height: 20vh;
}
.text {
    text-align: center;
    font-size: var(--font-size-high-medium);
    font-variant-numeric: lining-nums;
}
.card-content {
    background-color: var(--white);
    padding: 10px;
    font-size: var(--font-size-high-medium);
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    justify-content: center;
}
.accent {
    color: var(--red);
}
</style>
